import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  doc, 
  query, 
  where, 
  orderBy, 
  limit 
} from 'firebase/firestore';
import { db } from '../firebase.js';

// Firebase service for direct Firestore access when API is unavailable
export class FirebaseService {
  // Cash Bills
  static async getCashBills(companyName = null, limitCount = 10) {
    try {
      
      // Use simple query without composite index to avoid Firebase index requirements
      let q = collection(db, 'cashbills');
      
      const querySnapshot = await getDocs(q);
      let bills = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        bills.push({
          id: doc.id,
          _id: doc.id,
          ...data
        });
      });
      
      
      // Client-side filtering and sorting to avoid Firebase index requirements
      if (companyName) {
        const originalLength = bills.length;
        let filteredBills = bills.filter(bill => {
          const billCompanyName = bill.company?.name || bill.companyName || bill.selectedCompany;
          const matches = billCompanyName === companyName;
          return matches;
        });
        
        // If no exact match found, try partial matching
        if (filteredBills.length === 0 && originalLength > 0) {
          filteredBills = bills.filter(bill => {
            const billCompanyName = bill.company?.name || bill.companyName || bill.selectedCompany;
            const partialMatch = billCompanyName && billCompanyName.toLowerCase().includes(companyName.toLowerCase());
            return partialMatch;
          });
        }
        
        // If still no match, return all bills for debugging
        if (filteredBills.length === 0 && originalLength > 0) {
          filteredBills = bills;
        }
        
        bills = filteredBills;
      }
      
      // Client-side sorting by date
      bills.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.date || 0);
        const dateB = new Date(b.createdAt || b.date || 0);
        return dateB - dateA;
      });
      
      // Client-side limit (fetch more to ensure we have enough for latest 5)
      bills = bills.slice(0, Math.max(limitCount, 10));
      
      return bills;
    } catch (error) {
      console.error('Firebase: Error fetching cash bills:', error);
      return [];
    }
  }

  static async addCashBill(billData) {
    try {
      const docRef = await addDoc(collection(db, 'cashbills'), {
        ...billData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      return {
        _id: docRef.id,
        id: docRef.id,
        ...billData
      };
    } catch (error) {
      console.error('Firebase: Error adding cash bill:', error);
      throw error;
    }
  }

  // Credit Bills
  static async getCreditBills(companyName = null, limitCount = 10) {
    try {
      
      let q = collection(db, 'creditbills');
      
      const querySnapshot = await getDocs(q);
      let bills = [];
      
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Firebase: Checking bill document:', {
          docId: doc.id,
          companyInDoc: data.company?.name,
          searchingFor: companyName,
          matches: data.company?.name === companyName
        });
        
        // Filter by company if specified
        if (!companyName || data.company?.name === companyName) {
          bills.push({
            id: doc.id,
            _id: doc.id,
            ...data
          });
        }
      });
      
      // Sort by creation date (newest first) and limit
      bills.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      
      return bills.slice(0, limitCount);
    } catch (error) {
      console.error('Firebase: Error fetching credit bills:', error);
      console.error('Firebase: Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      return [];
    }
  }

  static async addCreditBill(billData) {
    try {
      const docRef = await addDoc(collection(db, 'creditbills'), {
        ...billData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      return {
        _id: docRef.id,
        id: docRef.id,
        ...billData
      };
    } catch (error) {
      console.error('Firebase: Error adding credit bill:', error);
      throw error;
    }
  }

  static async getLatestCreditBillInvoice(companyName) {
    try {
      const bills = await this.getCreditBills(companyName, 1);
      if (bills.length > 0) {
        const latestBill = bills[0];
        const invoiceNo = latestBill.invoiceNo;
        
        // Extract number and increment
        const match = invoiceNo?.match(/([A-Z]+)-(\d+)/);
        if (match) {
          const prefix = match[1];
          const number = parseInt(match[2]);
          const nextNumber = number + 1;
          return `${prefix}-${nextNumber}`;
        }
      }
      
      // Fallback to company prefix + 1
      const companyPrefixes = {
        'WYENFOS INFOTECH': 'WIT',
        'WYENFOS GOLD AND DIAMONDS': 'WGD',
        'WYENFOS ADS': 'WAD',
        'WYENFOS CASH VAPASE': 'WCV',
        'AYUR FOR HERBALS INDIA': 'ALH',
        'WYENFOS': 'WNF',
        'WYENFOS PURE DROPS': 'WPD',
      };
      
      const prefix = companyPrefixes[companyName] || 'WNF';
      return `${prefix}-1`;
    } catch (error) {
      console.error('Firebase: Error getting latest credit bill invoice:', error);
      return 'WNF-1';
    }
  }

  // Products
  static async getProducts(companyName = null) {
    try {
      // Use simple query to avoid index requirements
      const q = collection(db, 'products');
      const querySnapshot = await getDocs(q);
      let products = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        products.push({
          id: doc.id,
          _id: doc.id,
          ...data
        });
      });
      
      // Client-side filtering if company specified
      if (companyName) {
        products = products.filter(product => 
          product.company === companyName || 
          product.company?.name === companyName
        );
      }
      
      return products;
    } catch (error) {
      console.error('Firebase: Error fetching products:', error);
      return [];
    }
  }

  static async addProduct(productData) {
    try {
      const docRef = await addDoc(collection(db, 'products'), {
        ...productData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      console.log('Firebase: Product added with ID:', docRef.id);
      return {
        _id: docRef.id,
        id: docRef.id,
        ...productData
      };
    } catch (error) {
      console.error('Firebase: Error adding product:', error);
      throw error;
    }
  }

  // Customers
  static async getCustomers(searchQuery = null) {
    try {
      let q = collection(db, 'customers');
      
      if (searchQuery) {
        // Firebase doesn't support full-text search, so we'll get all and filter
        q = query(collection(db, 'customers'));
      } else {
        q = query(
          collection(db, 'customers'),
          orderBy('createdAt', 'desc'),
          limit(100)
        );
      }
      
      const querySnapshot = await getDocs(q);
      let customers = [];
      
      querySnapshot.forEach((doc) => {
        customers.push({
          id: doc.id,
          _id: doc.id,
          ...doc.data()
        });
      });
      
      // Client-side filtering for search
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        customers = customers.filter(customer => 
          customer.customerName?.toLowerCase().includes(searchLower) ||
          customer.customerId?.toLowerCase().includes(searchLower) ||
          customer.customerContact?.phone?.includes(searchQuery) ||
          customer.customerContact?.email?.toLowerCase().includes(searchLower)
        );
      }
      
      console.log('Firebase: Fetched customers:', customers.length);
      return customers;
    } catch (error) {
      console.error('Firebase: Error fetching customers:', error);
      return [];
    }
  }

  static async addCustomer(customerData) {
    try {
      // Generate customer ID by checking existing customers
      const existingCustomers = await this.getCustomers();
      const lastCustomerId = existingCustomers.length > 0 
        ? Math.max(...existingCustomers.map(c => {
            const match = c.customerId?.match(/CUST-(\d+)/);
            return match ? parseInt(match[1]) : 0;
          }))
        : 0;
      const nextId = lastCustomerId + 1;
      
      const customerWithId = {
        ...customerData,
        customerId: `CUST-${nextId}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      const docRef = await addDoc(collection(db, 'customers'), customerWithId);
      
      console.log('Firebase: Customer added with ID:', docRef.id, 'Customer ID:', customerWithId.customerId);
      return {
        _id: docRef.id,
        id: docRef.id,
        ...customerWithId
      };
    } catch (error) {
      console.error('Firebase: Error adding customer:', error);
      throw error;
    }
  }

  static async updateCustomer(customerId, customerData) {
    try {
      // Find the document with the matching customerId
      const q = query(collection(db, 'customers'), where('customerId', '==', customerId));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const customerDoc = querySnapshot.docs[0];
        const updatedData = {
          ...customerData,
          updatedAt: new Date().toISOString()
        };
        
        await updateDoc(doc(db, 'customers', customerDoc.id), updatedData);
        console.log('Firebase: Customer updated:', customerId);
        return true;
      } else {
        console.log('Firebase: Customer not found for update:', customerId);
        return false;
      }
    } catch (error) {
      console.error('Firebase: Error updating customer:', error);
      throw error;
    }
  }

  static async deleteCustomer(customerId) {
    try {
      // Find the document with the matching customerId
      const q = query(collection(db, 'customers'), where('customerId', '==', customerId));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const customerDoc = querySnapshot.docs[0];
        await deleteDoc(doc(db, 'customers', customerDoc.id));
        console.log('Firebase: Customer deleted:', customerId);
        return true;
      } else {
        console.log('Firebase: Customer not found for deletion:', customerId);
        return false;
      }
    } catch (error) {
      console.error('Firebase: Error deleting customer:', error);
      throw error;
    }
  }

  // Companies
  static async getCompanies() {
    try {
      const q = query(collection(db, 'companies'));
      const querySnapshot = await getDocs(q);
      const companies = [];
      
      querySnapshot.forEach((doc) => {
        companies.push({
          id: doc.id,
          _id: doc.id,
          ...doc.data()
        });
      });
      
      console.log('Firebase: Fetched companies:', companies.length);
      return companies;
    } catch (error) {
      // Handle permission errors more gracefully
      if (error.code === 'permission-denied' || error.message.includes('Missing or insufficient permissions')) {
        console.warn('Firebase: Permission denied for companies collection, using fallback data');
      } else {
        console.error('Firebase: Error fetching companies:', error);
      }
      return [];
    }
  }

  // Notifications
  static async addNotification(notificationData) {
    try {
      const docRef = await addDoc(collection(db, 'notifications'), {
        ...notificationData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      console.log('Firebase: Notification added with ID:', docRef.id);
      return {
        _id: docRef.id,
        id: docRef.id,
        ...notificationData
      };
    } catch (error) {
      console.error('Firebase: Error adding notification:', error);
      throw error;
    }
  }

  // Request Management
  static async addRequest(requestData) {
    try {
      const docRef = await addDoc(collection(db, 'requests'), {
        ...requestData,
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      console.log('Firebase: Request added with ID:', docRef.id);
      return {
        _id: docRef.id,
        id: docRef.id,
        ...requestData,
        status: 'pending'
      };
    } catch (error) {
      console.error('Firebase: Error adding request:', error);
      throw error;
    }
  }

  static async getRequests(filters = {}) {
    try {
      let q = collection(db, 'requests');
      
      // Apply filters if provided
      if (filters.requestedBy) {
        q = query(q, where('requestedBy', '==', filters.requestedBy));
      }
      if (filters.status) {
        q = query(q, where('status', '==', filters.status));
      }
      
      const querySnapshot = await getDocs(q);
      const requests = [];
      
      querySnapshot.forEach((doc) => {
        requests.push({
          id: doc.id,
          _id: doc.id,
          ...doc.data()
        });
      });
      
      console.log('Firebase: Found', requests.length, 'requests');
      return requests;
    } catch (error) {
      console.error('Firebase: Error fetching requests:', error);
      return [];
    }
  }

  // Bank Details
  static async getBankDetails(companyName) {
    try {
      console.log('Firebase: Fetching bank details for company:', companyName);
      
      // Simple query without retry logic to avoid ESLint warnings
      const q = query(
        collection(db, 'bankDetails'), 
        where('companyName', '==', companyName)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const bankDoc = querySnapshot.docs[0];
        const bankData = {
          id: bankDoc.id,
          _id: bankDoc.id,
          ...bankDoc.data()
        };
        
        console.log('Firebase: Found bank details for', companyName, bankData);
        return bankData;
      } else {
        console.log('Firebase: No bank details found for company:', companyName);
        return null;
      }
    } catch (error) {
      console.error('Firebase: Error fetching bank details after retries:', error);
      return null;
    }
  }

  // Credit Notes
  static async getCreditNotes(companyName = null, limitCount = 10) {
    try {
      let q = collection(db, 'creditnotes');
      
      const querySnapshot = await getDocs(q);
      let notes = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        notes.push({
          id: doc.id,
          _id: doc.id,
          ...data
        });
      });
      
      // Client-side filtering by company if specified
      if (companyName) {
        notes = notes.filter(note => {
          const noteCompanyName = note.company?.name || note.companyName || note.selectedCompany;
          return noteCompanyName === companyName;
        });
      }
      
      // Sort by creation date (newest first) and limit
      notes.sort((a, b) => new Date(b.createdAt || b.date || 0) - new Date(a.createdAt || a.date || 0));
      
      return notes.slice(0, limitCount);
    } catch (error) {
      console.error('Firebase: Error fetching credit notes:', error);
      return [];
    }
  }

  static async addCreditNote(noteData) {
    try {
      const docRef = await addDoc(collection(db, 'creditnotes'), {
        ...noteData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      return {
        _id: docRef.id,
        id: docRef.id,
        ...noteData
      };
    } catch (error) {
      console.error('Firebase: Error adding credit note:', error);
      throw error;
    }
  }

  // Debit Notes
  static async getDebitNotes(companyName = null, limitCount = 10) {
    try {
      let q = collection(db, 'debitnotes');
      
      const querySnapshot = await getDocs(q);
      let notes = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        notes.push({
          id: doc.id,
          _id: doc.id,
          ...data
        });
      });
      
      // Client-side filtering by company if specified
      if (companyName) {
        notes = notes.filter(note => {
          const noteCompanyName = note.company?.name || note.companyName || note.selectedCompany;
          return noteCompanyName === companyName;
        });
      }
      
      // Sort by creation date (newest first) and limit
      notes.sort((a, b) => new Date(b.createdAt || b.date || 0) - new Date(a.createdAt || a.date || 0));
      
      return notes.slice(0, limitCount);
    } catch (error) {
      console.error('Firebase: Error fetching debit notes:', error);
      return [];
    }
  }

  static async addDebitNote(noteData) {
    try {
      const docRef = await addDoc(collection(db, 'debitnotes'), {
        ...noteData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      return {
        _id: docRef.id,
        id: docRef.id,
        ...noteData
      };
    } catch (error) {
      console.error('Firebase: Error adding debit note:', error);
      throw error;
    }
  }

  // Generic collection getter
  static async getCollection(collectionName, filters = {}) {
    try {
      let q = collection(db, collectionName);
      
      // Apply filters if provided
      if (filters.where) {
        q = query(q, where(filters.where.field, filters.where.operator, filters.where.value));
      }
      
      if (filters.orderBy) {
        q = query(q, orderBy(filters.orderBy.field, filters.orderBy.direction || 'desc'));
      }
      
      if (filters.limit) {
        q = query(q, limit(filters.limit));
      }
      
      const querySnapshot = await getDocs(q);
      const items = [];
      
      querySnapshot.forEach((doc) => {
        items.push({
          id: doc.id,
          _id: doc.id,
          ...doc.data()
        });
      });
      
      console.log(`Firebase: Fetched ${collectionName}:`, items.length);
      return items;
    } catch (error) {
      console.error(`Firebase: Error fetching ${collectionName}:`, error);
      return [];
    }
  }
}

export default FirebaseService;

